/**
 * MapLinkButton - Botão compacto para abrir uma coordenada no Google Maps
 *
 * Extraído do InspectionDialog para reuso pela timeline da vistoria
 * (InspectionTimeline) e demais pontos do módulo, sem duplicar a lógica
 * de validação de coordenadas nem a abertura do link externo.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Extração do InspectionDialog (código inalterado).
 */
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/** Confirma coordenadas numéricas antes de criar links externos. */
function hasValidGeo(lat?: number | null, lng?: number | null) {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(Number(lat)) <= 90 && Math.abs(Number(lng)) <= 180;
}

/** Abre uma coordenada no Google Maps sem depender de chave de API. */
function openGoogleMaps(lat?: number | null, lng?: number | null) {
  if (!hasValidGeo(lat, lng)) return;
  window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`, '_blank', 'noopener,noreferrer');
}

/** Botão compacto e acessível para abrir a evidência geográfica. */
export function MapLinkButton({ lat, lng, label = 'Abrir localização no Google Maps' }: {
  lat?: number | null; lng?: number | null; label?: string;
}) {
  if (!hasValidGeo(lat, lng)) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-primary" aria-label={label}
          onClick={() => openGoogleMaps(lat, lng)}>
          <MapPin className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent className="text-xs">{label}</TooltipContent>
    </Tooltip>
  );
}
